import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import FieldError from "../../../components/ui/FieldError";
import RequiredField from "../../../components/ui/RequiredField";
import SelectCategories from "../../../components/ui/Selects/Categories";
import { Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import SelectCountries from "../../../components/ui/Selects/Countries";
import SelectPartnerLogisticAddress from "../../../components/ui/Selects/PartnerLogisticAddress";
import Button from "../../../components/ui/Button";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import fr from "date-fns/locale/fr";
import en from "date-fns/locale/en-US";

function ItemForm({ item, form, onSubmit, onCancel = null }) {
  const authUser = useSelector((state) => state.auth);
  const isAdmin = authUser?.user?.roles.includes("ROLE_ADMIN");
  const [selectedCategory, setSelectedCategory] = useState(
    (item && item.itemCategory["@id"]) || null
  );
  const { t, i18n } = useTranslation();

  if (i18n.language === "en") {
    registerLocale("en", en);
  } else {
    registerLocale("fr", fr);
  }

  const itemSubCategoryRef = useRef();
  const itemCategoryRef = useRef();
  return (
    form && (
      <>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 lg:col-span-6">
              <label>
                {t("categories.name")} <RequiredField /> :
              </label>
              <input
                {...form.register("name")}
                name="name"
                type="text"
                className="w-full"
                defaultValue={item?.name || null}
                aria-invalid={form.formState?.errors?.name ? true : false}
              />
              <FieldError error={form.formState?.errors?.name} />
            </div>

            <div className="col-span-12 lg:col-span-6">
              <label>
                {t("items.partnerLogisticAddress")} <RequiredField /> :
              </label>

              <Controller
                control={form.control}
                name="logisticAddress"
                defaultValue={
                  item?.logisticAddress && item?.logisticAddress["@id"]
                }
                render={({ field: { onChange, ref } }) => (
                  <SelectPartnerLogisticAddress
                    onChange={onChange}
                    inputRef={ref}
                    className={"py-1"}
                    emptyOpt={false}
                    isClearable={false}
                    partner={
                      item ? item.partner["@id"] : authUser?.user?.partner
                    }
                    aria-invalid={
                      form.formState?.errors?.logisticAddress ? true : false
                    }
                    setValue={(v) => form.setValue("logisticAddress", v)}
                  />
                )}
              />

              <FieldError error={form.formState?.errors?.logisticAddress} />
            </div>

            <div className="col-span-12 lg:col-span-12">
              <label>
                {t("items.description")} <RequiredField /> :
              </label>

              <textarea
                type="text"
                {...form.register("description")}
                className="w-full"
                rows="5"
                name="description"
                defaultValue={item?.description || null}
                aria-invalid={
                  form.formState?.errors?.description ? true : false
                }
              />

              <FieldError error={form.formState?.errors?.description} />
            </div>

            <div
              className={`${
                selectedCategory
                  ? "col-span-12 xl:col-span-4"
                  : "col-span-12 xl:col-span-6"
              }`}
            >
              <label>
                {t("categories.label")} <RequiredField /> :
              </label>
              <Controller
                control={form.control}
                name="itemCategory"
                defaultValue={item?.itemCategory && item.itemCategory["@id"]}
                render={({ field: { onChange, value, ref } }) => (
                  <SelectCategories
                    isClearable={true}
                    onChange={(e) => {
                      onChange(e);
                      setSelectedCategory(e ? e : null);
                      form.setValue("itemSubCategory", null);
                      itemSubCategoryRef.current &&
                        itemSubCategoryRef.current.clearValue();
                    }}
                    myRef={itemCategoryRef}
                    value={value}
                  />
                )}
              />
              <FieldError error={form.formState?.errors?.itemCategory} />
              {authUser?.user?.roles.includes("ROLE_USER") && !isAdmin && (
                <div className="text-xs">{t("items.category_missing")}</div>
              )}
            </div>

            {selectedCategory && (
              <div className="col-span-12 xl:col-span-4">
                <label>{t("categories.sub-categories.label")} :</label>

                <Controller
                  control={form.control}
                  name="itemSubCategory"
                  defaultValue={
                    item?.itemSubCategory && item?.itemSubCategory["@id"]
                  }
                  render={({ field: { onChange, value, ref } }) => (
                    <SelectCategories
                      isClearable={true}
                      parentCategory={selectedCategory}
                      onChange={(e) => {
                        onChange(e);
                        //form.setValue("itemCategory", selectedCategory);
                      }}
                      value={value}
                      myRef={itemSubCategoryRef}
                    />
                  )}
                />

                <FieldError error={form.formState?.errors?.itemCategory} />
              </div>
            )}

            <div
              className={`${
                selectedCategory
                  ? "col-span-12 xl:col-span-4"
                  : "col-span-12 xl:col-span-6"
              }`}
            >
              <label>{t("items.country")} :</label>

              <Controller
                name="country"
                control={form.control}
                childRef={form.register}
                defaultValue={item && item.country}
                render={({ field: { onChange, value } }) => (
                  <SelectCountries
                    onChange={onChange}
                    value={value}
                    isoCode={item && item.country}
                    isClearable={true}
                  />
                )}
              />

              <FieldError error={form.formState?.errors?.country} />
            </div>

            <div className="col-span-12 xl:col-span-4">
              <label>{t("items.themalConditions")} :</label>
              <input
                {...form.register("thermalConditions")}
                name="thermalConditions"
                type="text"
                className="w-full"
                defaultValue={item?.thermalConditions || null}
                aria-invalid={
                  form.formState?.errors?.thermalConditions ? true : false
                }
              />
              <FieldError error={form.formState?.errors?.thermalConditions} />
            </div>

            <div className="col-span-12 xl:col-span-4">
              <label>{t("items.batchNumber")} :</label>
              <input
                {...form.register("batchNumber")}
                name="batchNumber"
                type="text"
                className="w-full"
                defaultValue={item?.batchNumber || null}
                aria-invalid={
                  form.formState?.errors?.batchNumber ? true : false
                }
              />
              <FieldError error={form.formState?.errors?.batchNumber} />
            </div>

            <div className="col-span-12 xl:col-span-4">
              <label>{t("items.unCode")} :</label>
              <input
                {...form.register("unCode")}
                name="unCode"
                type="text"
                className="w-full"
                defaultValue={item?.unCode || null}
                aria-invalid={form.formState?.errors?.unCode ? true : false}
              />
              <FieldError error={form.formState?.errors?.unCode} />
            </div>

            <div className="col-span-12 xl:col-span-4">
              <label>{t("items.hsCode")} :</label>
              <input
                {...form.register("hsCode")}
                name="hsCode"
                type="text"
                className="w-full"
                defaultValue={item?.hsCode || null}
                aria-invalid={form.formState?.errors?.hsCode ? true : false}
              />
              <FieldError error={form.formState?.errors?.hsCode} />
            </div>

            <div className="col-span-12 xl:col-span-4">
              <label>{t("items.class")} :</label>
              <input
                {...form.register("class")}
                name="class"
                type="text"
                className="w-full"
                defaultValue={item?.class || null}
                aria-invalid={form.formState?.errors?.class ? true : false}
              />
              <FieldError error={form.formState?.errors?.class} />
            </div>

            <div className="col-span-12">
              <label>{t("items.comment")} :</label>

              <textarea
                type="text"
                {...form.register("comment")}
                className="w-full"
                rows="3"
                name="comment"
                defaultValue={item?.comment || null}
                aria-invalid={form.formState?.errors?.comment ? true : false}
              />

              <FieldError error={form.formState?.errors?.comment} />
            </div>

            <div className="col-span-3">
              <label>{t("items.useByDate")} :</label>

              <Controller
                control={form.control}
                name="useByDate"
                defaultValue={item?.useByDate ? new Date(item.useByDate) : null}
                render={({ field: { onChange, ref, value } }) => (
                  <DatePicker
                    onChange={onChange}
                    selected={value}
                    inputRef={ref}
                    dateFormat={"dd/MM/yyyy"}
                    className="text-center"
                    locale={i18n.language}
                    aria-invalid={
                      form.formState?.errors?.useByDate ? true : false
                    }
                  />
                )}
              />

              <FieldError error={form.formState?.errors?.useByDate} />
            </div>
          </div>

          <div className="border-t mt-6 text-center mb-6">
            {onCancel && (
              <span
                className="cursor-pointer btn btn-default mt-6 mr-5"
                onClick={onCancel}
              >
                {t("global.btn.cancel")}
              </span>
            )}

            <Button
              className="btn btn-primary mt-6"
              isloading={form.formState.isSubmitting}
            >
              {item ? t("global.btn.update") : t("items.btnCreate")}
            </Button>
          </div>
        </form>
      </>
    )
  );
}

export default ItemForm;
